import React, { Component } from "react";
import { Box, Container, Grid, Typography } from "@mui/material";
import AnalyticsDispatcher from "dispatchers/analytics-dispatcher";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import Spinner from "util/components/spinner";
import CampaignOverview from "visual-components/analytics/campaign-overview";
import StatsByPartnerCard from "visual-components/analytics/stats-by-partner-card";
import ExplorePageTitle from "visual-components/shared/explore/explore-page-title";
import ClickContribution from "./campaign/ClickContribution";
import EntryContribution from "./campaign/EntryContribution";
import PromoEntries from "./campaign/PromoEntries";
import SocialActionClicks from "./campaign/SocialActionClicks";
import SocialContribution from "./campaign/SocialContribution";
import SocialGrowth from "./campaign/SocialGrowth";

// import CampaignConversion from './campaign/CampaignConversion'

const mapState = (state) => {
  return {
    analyticsCampaign: state.analyticsCampaign,
    currentBrand: state.currentBrand,
  };
};

class CampaignAnalytics extends Component {
  componentDidMount() {
    const {
      history,
      match: {
        params: { campaignId },
      },
    } = this.props;

    AnalyticsDispatcher.loadDefaultCampaignAnalytics(campaignId);
  }

  componentWillUnmount() {
    store.dispatch({
      model: "analyticsCampaign",
      type: "CLEAR",
    });
  }

  navToCampaignQuickview = () => {
    const {
      history,
      match: {
        params: { campaignId },
      },
    } = this.props;
    history.push(`/partnerships/campaign/${campaignId}/quickview`);
  };

  exportCsv = () => {
    const { analyticsCampaign, currentBrand } = this.props;

    const socialActionsChart = analyticsCampaign.campaignSocialActionsByType.raw;
    const socialActionClicksByTitle = analyticsCampaign.campaignSocialActionClicksByTitle.raw || [];

    const minThresh = analyticsCampaign.campaignEntryContributionStatsByPartner.map((partner) => {
      if (analyticsCampaign.isHost) {
        return {
          brand: partner.accountname,
          entries: partner.entries,
          threshold: partner.threshold,
          percentage: `${(partner.percentage * 100).toFixed(2)}%`,
        };
      } else {
        return {
          brand: partner.accountname,
          entries: partner.entries,
          percentage: `${(partner.percentage * 100).toFixed(2)}%`,
        };
      }
    });

    const customCta = analyticsCampaign.campaignCustomCTA.data
      .map((item) => {
        if (analyticsCampaign.isHost) {
          return {
            brand: item.accountname,
            entries: item.entries,
            percentage: `${(item.percentage * 100).toFixed(2)}%`,
          };
        } else if (item.brandId == currentBrand.id) {
          return {
            brand: item.accountname,
            entries: item.entries,
            percentage: `${(item.percentage * 100).toFixed(2)}%`,
          };
        }
      })
      .filter((item) => item != undefined);

    store.dispatch({
      type: "UPDATE_ATTR",
      model: "exportAnalyticsModal",
      data: {
        active: true,
        filename: analyticsCampaign.campaign.id
          ? `campaign_analytics_${analyticsCampaign.campaign.id}.csv`
          : `campaign_analytics.csv`,
        metrics: [
          {
            label: "Line Chart - Visits",
            value: analyticsCampaign.campaignVisits.raw,
          },
          {
            label: "Line Chart - Entries",
            value: analyticsCampaign.campaignEntries.raw,
          },
          {
            label: "Line Chart - New To File",
            value: analyticsCampaign.campaignNTF.raw,
          },
          {
            label: "Line Chart - Social Actions",
            value: analyticsCampaign.campaignTotalSocialActions.raw,
          },
          {
            label: analyticsCampaign.isHost ? "Minimum Threshold" : "Entry Contribution",
            value: minThresh,
          },
          {
            label: "Stats by Partner",
            value: analyticsCampaign.campaignStatsByPartner.raw,
          },
          {
            label: `Social Actions ${analyticsCampaign.isHost ? "- All Partners" : ""}`,
            value: socialActionsChart,
          },
          {
            label: `Social Action Clicks ${analyticsCampaign.isHost ? "- All Partners" : ""}`,
            value: socialActionClicksByTitle,
          },
          {
            label: `Custom CTA ${analyticsCampaign.isHost ? "- All Partners" : ""}`,
            value: customCta,
          },
          // {
          //   label: 'Entries - Geographic Distribution',
          //   value: geographicDist
          // },
          // {
          //   label: "Entries - Gender Split",
          //   value: [campaignDemographics ? campaignDemographics.gender_distribution : null],
          // },
          // {
          //   label: "Entries - Household Income",
          //   value: incomeDist,
          // },
          // {
          //   label: "Conversions",
          //   value: analyticsCampaign.campaignConversionDetails.csv,
          // },
          {
            label: "Entries By Channel",
            value: analyticsCampaign.campaignEntryContributionStatsByPartnerByPromoUnit.csv,
          },
        ],
      },
    });
  };

  render() {
    const {
      analyticsCampaign,
      currentBrand: {
        features: { block_sms },
      },
    } = this.props;
    const canViewSms = !block_sms;

    if (analyticsCampaign.loading) {
      return <Spinner />;
    }

    const statsByPartner = analyticsCampaign.campaignStatsByPartner;
    const campaignStarted =
      new Date(analyticsCampaign.campaign.startDate).getTime() <= new Date().getTime();

    const solo = analyticsCampaign.campaign.isSolo;

    return (
      <Container maxWidth="lg" sx={{ py: 4, bgcolor: "#FBFBFB" }}>
        <Helmet>
          <title>Campaign Analytics</title>
        </Helmet>

        <Grid container spacing={3} alignItems="stretch">
          {/* Header */}
          <Grid size={12}>
            <ExplorePageTitle
              title={analyticsCampaign.campaignName}
              action={this.exportCsv}
              actionLabel="Export CSV"
              secondaryAction={this.navToCampaignQuickview}
              secondaryActionLabel="Manage Campaign"
              secondaryPrimaryColor
            />
          </Grid>

          {/* Description for non-solo campaigns */}
          {!solo && (
            <Grid size={12}>
              <Typography variant="h5" color="text.secondary">
                Use this detailed view to understand how specific partners performed in your
                campaign.
              </Typography>
            </Grid>
          )}

          {/* Campaign Overview & Entry Contribution */}
          <Grid size={{ xs: 12, md: 7 }}>
            <CampaignOverview
              {...analyticsCampaign}
              canViewSms={canViewSms}
              started={campaignStarted}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <EntryContribution started={campaignStarted} />
          </Grid>

          {/* Stats by Partner */}
          {!solo && (
            <Grid size={12}>
              <StatsByPartnerCard
                canViewSms={canViewSms}
                data={statsByPartner}
                started={campaignStarted}
              />
            </Grid>
          )}

          {/* Social Growth & Social Contribution */}
          <Grid size={{ xs: 12, md: 7 }}>
            <SocialGrowth />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <SocialContribution started={campaignStarted} />
          </Grid>

          {/* Social Action Clicks & Click Contribution */}
          <Grid size={{ xs: 12, md: 7 }}>
            <SocialActionClicks started={campaignStarted} />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <ClickContribution started={campaignStarted} />
          </Grid>

          {/* Promo Entries */}
          <Grid size={7}>
            <PromoEntries />
          </Grid>
        </Grid>
      </Container>
    );
  }
}

export default withRouter(connect(mapState)(CampaignAnalytics));
